/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * Changes Settings Component
 *
 * Settings panel for the Changes page UI preferences.
 * Allows toggling between Simple Mode (unified sync button) and
 * Advanced Mode (separate sync sections).
 */

import { useState, useEffect, useMemo } from 'react';
import { GitBranch, RefreshCw } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { SkillShopService } from '../../services/skillShopService';

export function ChangesSettings() {
  const { toast } = useToast();
  const skillShopService = useMemo(() => new SkillShopService(), []);

  const [loading, setLoading] = useState(true);
  const [changesSimpleMode, setChangesSimpleMode] = useState(true);
  const [saving, setSaving] = useState(false);

  // Load current setting on mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const userSettings = await skillShopService.getUserSettings();
        const velocitySettings = userSettings?.integrationSettings?.productApp?.velocitySettings;
        // Default to true if not set
        setChangesSimpleMode(velocitySettings?.changesSimpleMode ?? true);
      } catch (error) {
        console.error('[ChangesSettings] Failed to load settings:', error);
      } finally {
        setLoading(false);
      }
    };
    loadSettings();
  }, [skillShopService]);

  const handleToggle = async () => {
    const newValue = !changesSimpleMode;
    setChangesSimpleMode(newValue);
    setSaving(true);

    try {
      await skillShopService.updateVelocitySettings({
        changesSimpleMode: newValue,
      });
      toast({
        title: 'Settings Updated',
        description: newValue
          ? 'Changes page set to Simple Mode'
          : 'Changes page set to Advanced Mode',
      });
    } catch (error) {
      // Revert on failure
      setChangesSimpleMode(!newValue);
      toast({
        title: 'Failed to Save',
        description: 'Could not update Changes settings',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        padding: '20px',
        background: '#f9f9f9',
        borderRadius: '8px',
        border: '1px solid #ddd',
        marginBottom: '32px',
        textAlign: 'center',
        color: '#6b7280',
      }}>
        <RefreshCw size={20} className="spin" style={{ marginBottom: '8px' }} />
        <p style={{ margin: 0, fontSize: '14px' }}>Loading Changes settings...</p>
      </div>
    );
  }

  return (
    <div style={{
      marginBottom: '32px',
      padding: '20px',
      background: '#f9f9f9',
      borderRadius: '8px',
      border: '1px solid #ddd',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
        <GitBranch size={20} style={{ marginRight: '8px', color: '#0066cc' }} />
        <h3 style={{ margin: 0 }}>Changes Settings</h3>
      </div>

      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px',
        background: '#fff',
        borderRadius: '6px',
        border: '1px solid #e5e7eb',
      }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '4px' }}>
            Changes Simple Mode
          </div>
          <div style={{ fontSize: '13px', color: '#6b7280' }}>
            {changesSimpleMode
              ? 'Showing a single unified "Sync With Main" button for push and pull operations'
              : 'Showing separate "Sync To Main" and "Sync With Main" sections for advanced control'}
          </div>
        </div>
        <button
          onClick={handleToggle}
          disabled={saving}
          style={{
            position: 'relative',
            width: '44px',
            height: '24px',
            borderRadius: '12px',
            border: 'none',
            cursor: saving ? 'not-allowed' : 'pointer',
            background: changesSimpleMode ? '#0066cc' : '#d1d5db',
            transition: 'background 0.2s ease',
            flexShrink: 0,
            marginLeft: '16px',
            opacity: saving ? 0.6 : 1,
          }}
          aria-label="Toggle Changes Simple Mode"
          role="switch"
          aria-checked={changesSimpleMode}
        >
          <span style={{
            position: 'absolute',
            top: '2px',
            left: changesSimpleMode ? '22px' : '2px',
            width: '20px',
            height: '20px',
            borderRadius: '50%',
            background: '#fff',
            boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
            transition: 'left 0.2s ease',
          }} />
        </button>
      </div>

      <div style={{
        marginTop: '16px',
        padding: '12px',
        background: '#e9ecef',
        borderRadius: '4px',
        fontSize: '13px',
        color: '#495057',
      }}>
        <strong>Current Mode:</strong> {changesSimpleMode ? 'Simple' : 'Advanced'} — {changesSimpleMode
          ? 'A single button syncs changes in both directions safely.'
          : 'Separate controls for pushing to and pulling from main.'}
      </div>
    </div>
  );
}
